import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button, Card, Space, Tag, Tooltip, Typography, notification,
} from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from 'util/api.js';
import { formatCacheAge, formatRowCount } from 'util/formatCacheAge';
import { invalidatePricingDiscrepancyQueries } from './use-pricing-discrepancy-scan-query';
import { errorMessage, SHOPIFY_COMPARED_STORE, SHOPIFY_READONLY_STORE } from './helpers';

const { Text } = Typography;

const KOUNTA_IMPORT = '/dashboard/product-database/monday-integration?tab=product-cache';

function shopifyStore(fresh, store) {
  const stores = fresh?.shopify?.stores;
  if (!Array.isArray(stores)) return null;
  return stores.find((s) => s.store === store) || null;
}

function staleLevel(iso) {
  if (!iso) return 'empty';
  const days = dayjs().diff(dayjs(iso), 'day');
  if (days >= 30) return 'stale';
  if (days >= 7) return 'aging';
  return 'fresh';
}

function StaleTag({ level }) {
  if (level === 'stale') return <Tag color="error">Stale</Tag>;
  if (level === 'aging') return <Tag color="warning">Aging</Tag>;
  if (level === 'empty') return <Tag>No data</Tag>;
  return <Tag color="success">Fresh</Tag>;
}

StaleTag.propTypes = { level: PropTypes.string.isRequired };

function PlatformCell({
  name, asOf, live, rowCount, level, action,
}) {
  let when = 'No data';
  if (live) when = 'Live';
  else if (asOf) when = formatCacheAge(asOf);
  const rows = rowCount == null ? null : `${formatRowCount(rowCount)} rows`;
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      gap: 4,
    }}
    >
      <Space size={8} align="center" wrap>
        <Text strong>{name}</Text>
        {live ? <Tag color="blue">Live</Tag> : <StaleTag level={level} />}
      </Space>
      <Text type={!live && level === 'stale' ? 'danger' : 'secondary'}>{when}</Text>
      {rows ? (
        <Text type="secondary" style={{ fontSize: 12 }}>{rows}</Text>
      ) : null}
      {action ? <div style={{ marginTop: 'auto', paddingTop: 8 }}>{action}</div> : null}
    </div>
  );
}

PlatformCell.propTypes = {
  name: PropTypes.string.isRequired,
  asOf: PropTypes.string,
  live: PropTypes.bool,
  rowCount: PropTypes.number,
  level: PropTypes.string,
  action: PropTypes.node,
};

PlatformCell.defaultProps = {
  asOf: null,
  live: false,
  rowCount: null,
  level: 'fresh',
  action: null,
};

/**
 * One card per price source, same layout, with a refresh where one exists.
 * Kounta comparison data is CSV-imported: there is no pull that refreshes it.
 */
export default function PlatformStatusGrid({ sourceFreshness, disabled }) {
  const queryClient = useQueryClient();
  const [cin7Syncing, setCin7Syncing] = useState(false);
  const gpnz = shopifyStore(sourceFreshness, SHOPIFY_COMPARED_STORE);
  const gpau = shopifyStore(sourceFreshness, SHOPIFY_READONLY_STORE);

  const refreshMonday = useMutation({
    mutationFn: () => api.refreshMondayPriceCache(),
    onSuccess: (data) => {
      invalidatePricingDiscrepancyQueries(queryClient);
      notification.success({
        message: `Monday price cache refreshed (${data?.upsertedRows ?? 0} SKU row(s)).`,
      });
    },
    onError: (err) => notification.error({ message: errorMessage(err, 'Monday refresh failed') }),
  });

  const refreshStore = useMutation({
    mutationFn: (store) => api.refreshShopifyPriceCache(store),
    onSuccess: (data, store) => {
      invalidatePricingDiscrepancyQueries(queryClient);
      notification.success({
        message: `Shopify ${store} refreshed (${data?.products ?? 0} products). Recompute to compare.`,
      });
    },
    onError: (err) => notification.error({ message: errorMessage(err, 'Shopify refresh failed') }),
  });

  const refreshCin7 = async () => {
    setCin7Syncing(true);
    try {
      const start = await api.syncCin7Products(false);
      if (start.status === 403) {
        notification.error({
          message: 'Cin7 refresh needs product-cache or monday-integration permission.',
        });
        return;
      }
      if (start.status >= 400 && start.status !== 409) {
        notification.error({ message: errorMessage(start.data, 'Cin7 refresh failed') });
        return;
      }
      for (let i = 0; i < 200; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => { setTimeout(resolve, 3000); });
        // eslint-disable-next-line no-await-in-loop
        const state = await api.getCin7SyncStatus();
        if (state?.status !== 'running') {
          invalidatePricingDiscrepancyQueries(queryClient);
          if (state?.status === 'failed') {
            notification.error({ message: state.error || 'Cin7 sync failed' });
          } else {
            notification.success({ message: 'Cin7 product cache sync finished.' });
          }
          return;
        }
      }
      notification.warning({ message: 'Cin7 sync still running. Recompute in a few minutes.' });
    } catch (err) {
      notification.error({ message: errorMessage(err, 'Cin7 refresh failed') });
    } finally {
      setCin7Syncing(false);
    }
  };

  const cards = [
    {
      key: 'monday',
      name: 'Monday',
      asOf: sourceFreshness?.monday?.asOf,
      rowCount: sourceFreshness?.monday?.rowCount,
      action: (
        <Button
          size="small"
          disabled={disabled}
          loading={refreshMonday.isLoading}
          onClick={() => refreshMonday.mutate()}
        >
          Refresh
        </Button>
      ),
    },
    {
      key: 'cin7',
      name: 'Cin7',
      asOf: sourceFreshness?.cin7?.asOf,
      rowCount: sourceFreshness?.cin7?.rowCount,
      action: (
        <Button size="small" disabled={disabled} loading={cin7Syncing} onClick={refreshCin7}>
          Refresh
        </Button>
      ),
    },
    {
      key: 'gpnz',
      name: `Shopify ${SHOPIFY_COMPARED_STORE}`,
      asOf: gpnz?.asOf,
      rowCount: gpnz?.rowCount,
      action: (
        <Button
          size="small"
          disabled={disabled}
          loading={refreshStore.isLoading && refreshStore.variables === SHOPIFY_COMPARED_STORE}
          onClick={() => refreshStore.mutate(SHOPIFY_COMPARED_STORE)}
        >
          Refresh
        </Button>
      ),
    },
    {
      key: 'gpau',
      name: `Shopify ${SHOPIFY_READONLY_STORE}`,
      asOf: gpau?.asOf,
      rowCount: gpau?.rowCount,
      action: (
        <Button
          size="small"
          disabled={disabled}
          loading={refreshStore.isLoading && refreshStore.variables === SHOPIFY_READONLY_STORE}
          onClick={() => refreshStore.mutate(SHOPIFY_READONLY_STORE)}
        >
          Refresh
        </Button>
      ),
    },
    {
      key: 'kounta',
      name: 'Kounta',
      asOf: sourceFreshness?.kountaCsv?.asOf,
      rowCount: sourceFreshness?.kountaCsv?.rowCount,
      action: (
        <Tooltip title="Kounta prices on this page come from a CSV import. There is no API pull for this table.">
          <Link to={KOUNTA_IMPORT}>
            <Button size="small">Import CSV</Button>
          </Link>
        </Tooltip>
      ),
    },
  ];

  return (
    <Card size="small" title="Price sources">
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 16,
      }}
      >
        {cards.map((card) => (
          <PlatformCell
            key={card.key}
            name={card.name}
            asOf={card.asOf}
            live={card.live}
            rowCount={card.rowCount}
            level={card.live ? 'fresh' : staleLevel(card.asOf)}
            action={card.action}
          />
        ))}
      </div>
    </Card>
  );
}

PlatformStatusGrid.propTypes = {
  sourceFreshness: PropTypes.shape({
    monday: PropTypes.shape({ asOf: PropTypes.string, rowCount: PropTypes.number }),
    cin7: PropTypes.shape({ asOf: PropTypes.string, rowCount: PropTypes.number }),
    kountaCsv: PropTypes.shape({ asOf: PropTypes.string, rowCount: PropTypes.number }),
    shopify: PropTypes.shape({
      stores: PropTypes.arrayOf(PropTypes.shape({
        store: PropTypes.string,
        asOf: PropTypes.string,
        rowCount: PropTypes.number,
      })),
    }),
  }),
  disabled: PropTypes.bool,
};

PlatformStatusGrid.defaultProps = {
  sourceFreshness: null,
  disabled: false,
};
